import React, { useEffect, useMemo, useState } from "react";
import { Users, Clock, ArrowLeft } from "lucide-react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../../../firebase-config";
import { useAuth } from "../../../../../provider/Auth.provider";
import { useParams, useNavigate } from "react-router-dom";
import { findUserByUID } from "../../../../../service/findUsers/findUserByUID";
const goldGrad =
  "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black";

function GhostButton({ icon: Icon, children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 rounded-md border border-[#374151] px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition ${className}`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

function StatusPill({ status = "ongoing" }) {
  const styles = {
    ongoing: `${goldGrad} px-2 py-1 rounded-full text-xs font-bold`,
    upcoming:
      "bg-[#CF9F41] text-black px-2 py-1 rounded-full text-xs font-bold",
  };
  return <span className={styles[status] ?? styles.upcoming}>{status}</span>;
}

const CourseDetails = () => {
  const { user } = useAuth();
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [batchData, setBatchData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [buyItemIdsArray, setBuyItemIdsArray] = useState([]);
  const [coursesUI, setCoursesUI] = useState([]); // [{ key, courseId, courseName, description, image, batchId, status }]

  // fetch user buy items (to know which batch(es) the student purchased)
  useEffect(() => {
    const run = async () => {
      if (!user?.uid) return;
      try {
        const ud = await findUserByUID(user.uid);
        setBuyItemIdsArray(
          Array.isArray(ud?.buyItemIdsArray) ? ud.buyItemIdsArray : []
        );
      } catch (e) {
        console.error("buy items load failed", e);
        setBuyItemIdsArray([]);
      }
    };
    run();
  }, [user?.uid]);

  // fetch course/package data
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!packageId) {
        navigate("/student/dashboard");
        return;
      }
      try {
        const lmsPackageCollection = collection(db, "lms_package");
        let packageQuery = query(
          lmsPackageCollection,
          where("packageId", "==", packageId)
        );
        let packageSnapshot = await getDocs(packageQuery);

        if (packageSnapshot.empty) {
          packageQuery = query(
            lmsPackageCollection,
            where("id", "==", packageId)
          );
          packageSnapshot = await getDocs(packageQuery);
        }

        if (!packageSnapshot.empty) {
          const courseDoc = packageSnapshot.docs[0];
          setCourseData({ id: courseDoc.id, ...courseDoc.data() });
        } else {
          setCourseData(null);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
        setCourseData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourseData();
  }, [packageId, navigate]);

  // fetch batch docs referenced by the package
  useEffect(() => {
    const fetchBatchData = async () => {
      if (!courseData?.packageContainingBatches) return;
      const batchIds = courseData.packageContainingBatches.filter(
        (b) => typeof b === "string"
      );
      const obj = {};
      for (const id of batchIds) {
        try {
          const ref = doc(db, "lms_batch", id);
          const snap = await getDoc(ref);
          if (snap.exists()) obj[id] = { id: snap.id, ...snap.data() };
        } catch (e) {
          console.error("batch fetch error", id, e);
        }
      }
      setBatchData(obj);
    };
    fetchBatchData();
  }, [courseData?.packageContainingBatches]);

  // figure out which batch(es) this student purchased (status approved or pending)
  const purchasedBatchIds = useMemo(() => {
    const ids = buyItemIdsArray
      .filter(
        (x) =>
          x &&
          x.buyItemType === "batch" &&
          x.buyItemId &&
          (x.status === "approved" || x.status === "pending" || !x.status)
      )
      .map((x) => String(x.buyItemId));
    const pkgBatchIds = new Set(
      (courseData?.packageContainingBatches || [])
        .map((b) => (typeof b === "string" ? b : b?.id || b?.batchId))
        .filter(Boolean)
    );
    // only keep purchased batches that belong to this package
    return ids.filter((id) => pkgBatchIds.has(id));
  }, [buyItemIdsArray, courseData?.packageContainingBatches]);

  // build course list for UI from purchased batch(es)
  useEffect(() => {
    const loadCourses = async () => {
      if (!purchasedBatchIds.length) {
        setCoursesUI([]);
        return;
      }
      const seen = new Set();
      const out = [];

      for (const bid of purchasedBatchIds) {
        const b = batchData[bid];
        if (
          !b?.batchContainingCourses ||
          !Array.isArray(b.batchContainingCourses)
        )
          continue;

        // fetch each course doc to show nice titles/images
        for (const courseId of b.batchContainingCourses) {
          const key = `${bid}_${courseId}`;
          if (seen.has(key)) continue;
          seen.add(key);

          try {
            const cRef = doc(db, "lms_course", courseId);
            const cSnap = await getDoc(cRef);
            const cData = cSnap.exists() ? cSnap.data() : {};

            out.push({
              key,
              courseId,
              courseName: cData?.courseName || `Course ${courseId.slice(0, 6)}`,
              description:
                (cData?.courseDescription &&
                  String(cData.courseDescription)
                    .replace(/<[^>]*>/g, "")
                    .trim()) ||
                (courseData?.packageDescription &&
                  String(courseData.packageDescription)
                    .replace(/<[^>]*>/g, "")
                    .trim()) ||
                "Course description not available",
              image:
                cData?.courseBanner ||
                b?.batchBanner ||
                "https://via.placeholder.com/300x150/1f2937/ffffff?text=Course",
              batchId: bid,
              status: b?.batchStatus || "ongoing",
            });
          } catch (e) {
            console.error("course fetch error", courseId, e);
            out.push({
              key,
              courseId,
              courseName: `Course ${courseId.slice(0, 6)}`,
              description:
                (courseData?.packageDescription &&
                  String(courseData.packageDescription)
                    .replace(/<[^>]*>/g, "")
                    .trim()) ||
                "Course description not available",
              image:
                b?.batchBanner ||
                "https://via.placeholder.com/300x150/1f2937/ffffff?text=Course",
              batchId: bid,
              status: b?.batchStatus || "ongoing",
            });
          }
        }
      }

      setCoursesUI(out);
    };
    loadCourses();
  }, [purchasedBatchIds, batchData, courseData?.packageDescription]);

  const isLive = courseData?.packageType === "package_lc";

  // learning points cleaner
  const getLearningColumns = (raw) => {
    let points = raw || "";
    if (
      typeof points !== "string" ||
      points.length < 10 ||
      /xyzkm|llllllllll/i.test(points)
    ) {
      points = [
        "React Hooks fundamentals (useState, useEffect)",
        "Custom hook development",
        "Real-world project implementation",
        "Advanced hooks (useContext, useReducer)",
        "Performance optimization techniques",
        "Live coding sessions with experts",
      ];
    } else {
      points = points
        .replace(/<p[^>]*>/gi, "")
        .replace(/<\/p>/gi, "\n")
        .replace(/<li[^>]*>/gi, "")
        .replace(/<\/li>/gi, "\n")
        .replace(/<ul[^>]*>/gi, "")
        .replace(/<\/ul>/gi, "")
        .replace(/<ol[^>]*>/gi, "")
        .replace(/<\/ol>/gi, "")
        .replace(/<br\s*\/?>(?=\s*\n?)/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+\n/g, "\n")
        .trim()
        .split(/\n+/)
        .map((p) => p.replace(/^[•*\-]\s*/, "").trim())
        .filter(Boolean);
      if (!points.length) return getLearningColumns("");
    }
    const mid = Math.ceil(points.length / 2);
    return [points.slice(0, mid), points.slice(mid)];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black p-4 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-center py-24">
          <div className="flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2A2A2A] border-t-[#CF9F41]" />
            <p className="text-sm text-gray-300">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-screen bg-black p-4 text-white">
        <div className="mx-auto max-w-3xl rounded-lg border border-[#2A2A2A] bg-[#111111] p-6">
          <h2 className="mb-2 text-lg font-semibold text-[#CF9F41]">
            Course not found
          </h2>
          <GhostButton
            icon={ArrowLeft}
            onClick={() => navigate("/student/dashboard")}
          >
            Back to My Packages
          </GhostButton>
        </div>
      </div>
    );
  }

  const [leftCol, rightCol] = getLearningColumns(courseData.learningPoints);
  console.log(courseData, "getting");
  return (
    <div className="min-h-screen bg-black p-4 text-white">
      {/* Back */}
      <div className="mx-auto mb-6 max-w-6xl">
        <GhostButton
          icon={ArrowLeft}
          onClick={() => navigate("/student/dashboard")}
        >
          Back to My Packages
        </GhostButton>
      </div>

      {/* Header Card */}
      <div className="mx-auto mb-10 max-w-6xl rounded-xl border border-[#2A2A2A] bg-[#0F0F0F] p-4 sm:p-6">
        {/* Title Row & LIVE */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              {courseData.packageName || "React Hooks Deep Dive"}
            </h1>
            <p className="mt-1 text-sm text-gray-400">
              {courseData.packageDifficulty || "Advanced"} React Development
              Course
            </p>
          </div>
          {isLive && (
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${goldGrad}`}
            >
              LIVE
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left: image */}
          <div>
            <div className="overflow-hidden rounded-lg">
              <img
                src={
                  courseData.packageBanner ||
                  "https://via.placeholder.com/640x360/1f2937/ffffff?text=Course"
                }
                alt="Course"
                className="h-56 w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://via.placeholder.com/640x360/1f2937/ffffff?text=Course";
                }}
              />
            </div>
          </div>

          {/* Right: description + stats */}
          <div className="flex flex-col gap-6">
            {/* <div>
              <h3 className="mb-2 text-sm font-semibold">Course Description</h3>
              <div
                className="prose prose-invert max-w-none text-sm text-gray-300"
                dangerouslySetInnerHTML={{
                  __html:
                    courseData.packageDescription ||
                    'Master React Hooks in this comprehensive live course. Learn useState, useEffect, useContext, and build custom hooks. Perfect for developers looking to level up their React skills with modern patterns and best practices.',
                }}
              />
            </div> */}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-lg bg-[#2A2A2A] p-3">
                <Users className="h-5 w-5 text-[#CF9F41]" />
                <div className="leading-tight">
                  <div className="text-base font-bold text-[#CF9F41]">
                    {courseData.totalStudents || "65"}+
                  </div>
                  <div className="text-xs text-gray-300">Total Students</div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-[#2A2A2A] p-3">
                <Clock className="h-5 w-5 text-[#CF9F41]" />
                <div className="leading-tight">
                  <div className="text-base font-bold text-[#CF9F41]">
                    {courseData.packageDuration || "6 Weeks"}
                  </div>
                  <div className="text-xs text-gray-300">Course Duration</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What You'll Learn */}
        <div className="mt-8">
          <h2 className="mb-4 text-xl font-semibold">What You'll Learn</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[leftCol, rightCol].map((col, i) => (
              <ul key={i} className="space-y-3">
                {col.map((point, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-sm text-gray-300"
                  >
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-[#F3E681]" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>

      {/* Available Courses (from your purchased batch) */}
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-xl font-semibold">Available Courses</h2>
        <p className="mb-6 text-center text-gray-400">
          Showing courses from your purchased batch
          {purchasedBatchIds.length > 1 ? "es" : ""}.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {coursesUI.map((c) => (
            <div
              key={c.key}
              className="rounded-xl border border-[#2A2A2A] bg-[#111111] shadow-sm transition hover:-translate-y-0.5 hover:border-[#CF9F41]"
            >
              <div className="p-4">
                <div className="mb-3 overflow-hidden rounded-lg">
                  <img
                    src={c.image}
                    alt="Course"
                    className="h-28 w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://via.placeholder.com/300x150/1f2937/ffffff?text=Course";
                    }}
                  />
                </div>

                <div className="mb-2 flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold leading-5">
                    {c.courseName}
                  </h3>
                  <StatusPill status={c.status} />
                </div>

                <p className="mb-4 line-clamp-3 text-sm text-gray-300">
                  {c.description}
                </p>

                <button
                  onClick={() => {
                    navigate(
                      `/student/dashboard/mypackage/course/${packageId}/modules?batchId=${c.batchId}&courseId=${c.courseId}`
                    ); // then navigate
                  }}
                  className={`w-full rounded-lg px-4 py-2 text-sm font-semibold ${goldGrad} hover:from-[#CF9F41] hover:to-[#B17E32]`}
                >
                  Continue
                </button>
              </div>
            </div>
          ))}

          {coursesUI.length === 0 && (
            <div className="col-span-full rounded-xl border border-[#2A2A2A] bg-[#111111] p-6 text-center text-sm text-gray-400">
              No courses found for your purchased batch.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetails;
